import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL, PHONE, PHONE_HREF } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#08080A]/85 backdrop-blur-xl border-b border-[rgba(212,175,55,0.18)]" data-testid="navbar">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-[#D4AF37] focus:text-black focus:px-4 focus:py-2 focus:text-sm"
        data-testid="skip-to-content-link"
      >
        Skip to main content
      </a>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-16 sm:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link" aria-label="McMullen Construction Co. home">
            <img src={LOGO_URL} alt="McMullen Construction Co. logo" className="h-10 sm:h-12 w-auto rounded-sm gold-frame" />
            <span className="hidden md:block leading-tight">
              <span className="block font-display text-lg text-[#F4F2EE] tracking-wide">McMullen</span>
              <span className="block font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Construction Co.</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-[#F3E081]" : "text-[#A1A1AA] hover:text-[#F4F2EE]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a
              href={PHONE_HREF}
              data-testid="nav-phone-link"
              className="font-mono-accent text-xs tracking-[0.15em] text-[#D4AF37] hover:text-[#F3E081] transition-colors flex items-center gap-2"
            >
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              {PHONE}
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="border border-[rgba(212,175,55,0.5)] px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-[#F3E081] hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
            >
              Start Your Project
            </Link>
          </div>

          <button
            type="button"
            className="lg:hidden text-[#F4F2EE] p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            data-testid="nav-mobile-menu-button"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-[#08080A] flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-[rgba(212,175,55,0.18)]">
              <img src={LOGO_URL} alt="McMullen Construction Co. logo" className="h-10 w-auto rounded-sm" />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 text-[#F4F2EE]" data-testid="nav-mobile-close-button">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-8 pt-10" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}>
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-3 font-display text-3xl ${isActive || location.pathname === l.to ? "text-[#F3E081]" : "text-[#F4F2EE]"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.a
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                href={PHONE_HREF}
                className="mt-8 font-mono-accent text-sm tracking-[0.2em] text-[#D4AF37]"
                data-testid="nav-mobile-phone-link"
              >
                {PHONE}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
